import { useState, useEffect } from 'react';
import { Card, Col, Row, Statistic, Spin, Alert, Select, Space } from 'antd';
import {
  TeamOutlined,
  CheckCircleOutlined,
  StopOutlined,
  UserOutlined,
  CreditCardOutlined,
  DollarOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getAdminDashboardSummary, type AdminDashboardSummary } from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [data, setData] = useState<AdminDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trendMonths, setTrendMonths] = useState(6);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getAdminDashboardSummary({ trend_months: trendMonths })
      .then((d) => setData(d))
      .catch((err) => setError(err instanceof Error ? err.message : t('app.error')))
      .finally(() => setLoading(false));
  }, [trendMonths, t]);

  if (!hasPermission('admin', 'read')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!data) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <AdminNav />
      <Space style={{ marginBottom: 16 }} align="center">
        <h2 style={{ margin: 0 }}>{t('admin.dashboard_title')}</h2>
        <Select
          value={trendMonths}
          onChange={setTrendMonths}
          style={{ width: 120 }}
          options={[
            { value: 3, label: t('admin.dashboard.trend_3_months') },
            { value: 6, label: t('admin.dashboard.trend_6_months') },
            { value: 12, label: t('admin.dashboard.trend_12_months') },
            { value: 24, label: t('admin.dashboard.trend_24_months') },
          ]}
        />
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.total_tenants')}
              value={data.total_tenants}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.active_tenants')}
              value={data.active_tenants}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.suspended_tenants')}
              value={data.suspended_tenants}
              prefix={<StopOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.total_users')}
              value={data.total_users}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.active_subscriptions')}
              value={data.active_subscriptions}
              prefix={<CreditCardOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.total_revenue')}
              value={data.total_revenue.toFixed(2)}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('admin.dashboard.recent_audit_activity')}
              value={data.recent_audit_activity}
              prefix={<AuditOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title={t('admin.dashboard.tenants_by_plan')}>
            {data.tenants_by_plan.length === 0 ? (
              <span>{t('app.no_data')}</span>
            ) : (
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 0' }}>{t('admin.field.plan')}</th>
                    <th style={{ textAlign: 'right', padding: '8px 0' }}>{t('app.count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tenants_by_plan.map((item) => (
                    <tr key={item.key}>
                      <td style={{ padding: '4px 0' }}>{t(`admin.plan_options.${item.key}`, item.key)}</td>
                      <td style={{ textAlign: 'right', padding: '4px 0' }}>{item.doc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t('admin.dashboard.tenants_by_status')}>
            {data.tenants_by_status.length === 0 ? (
              <span>{t('app.no_data')}</span>
            ) : (
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 0' }}>{t('admin.field.status')}</th>
                    <th style={{ textAlign: 'right', padding: '8px 0' }}>{t('app.count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tenants_by_status.map((item) => (
                    <tr key={item.key}>
                      <td style={{ padding: '4px 0' }}>{t(`admin.status_options.${item.key}`, item.key)}</td>
                      <td style={{ textAlign: 'right', padding: '4px 0' }}>{item.doc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title={t('admin.dashboard.new_tenants_trend')}>
            {data.new_tenants_trend.length === 0 ? (
              <span>{t('app.no_data')}</span>
            ) : (
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 0' }}>{t('admin.field.created_at')}</th>
                    <th style={{ textAlign: 'right', padding: '8px 0' }}>{t('app.count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.new_tenants_trend.map((item) => (
                    <tr key={item.date}>
                      <td style={{ padding: '4px 0' }}>{item.date}</td>
                      <td style={{ textAlign: 'right', padding: '4px 0' }}>{item.doc_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
